import { ReactNode, useId, useState } from 'react';
import { Badge, Button, Radio, RadioGroup, Tooltip } from '@fluentui/react-components';
import { ChevronDown, ChevronUp, Info, SlidersHorizontal } from 'lucide-react';

import type { SignInMethod } from '../lib/activationReadiness';
import type { DeviceCodeBlockState } from '../lib/deviceCodeBlocking';
import { PLAIN_BUTTON, RADIUS, STROKE, SURFACE, TEXT } from '../theme/tokens';

// FD-612 — "Advanced sign-in options": the three sign-in paths as a radio list
// behind one disclosure, COLLAPSED by default. The default path is the Microsoft
// sign-in, which leads the dialog on its own; device code and a pasted token are
// for operators who need them.
//
// A real radiogroup (Fluent RadioGroup + Radio): arrow keys move between paths and
// a screen reader announces "radio, 1 of 3". ORDER IS THE DECISION (FD-ADR-047 §1):
// Microsoft first and recommended, device code second, a pasted token last.
//
// Selecting a path reveals ITS controls inline, under its own radio. The controls are
// the dialog's own (passed in), so no handler or validation lives here.
//
// A Conditional Access restriction on device code is a "Restricted by Conditional
// Access" badge with the policy names in a tooltip. It never disables the option:
// the probe is advisory and `activate-wave` refuses on its own terms (FD-296).

function policyNames(state: DeviceCodeBlockState): string {
  if (state.kind !== 'policies') return '';
  const list = state.covers.length > 0 ? state.covers : state.mayCover;
  return list.map((p) => p.displayName).join(', ');
}

const METHOD_NAMES: Record<SignInMethod, string> = {
  browser: 'Microsoft sign-in',
  'device-code': 'Device code',
  paste: 'Pasted token',
};

export interface AdvancedSignInOptionsProps {
  method: SignInMethod;
  onMethod: (m: SignInMethod) => void;
  /** `DRS_DEVICE_CODE_ENABLED=false` on this deployment. */
  guidedDisabled: boolean;
  blockState: DeviceCodeBlockState;
  deviceCodeBlocked: boolean;
  /** The selected path's own controls, rendered inline under its radio. */
  controls: Partial<Record<SignInMethod, ReactNode>>;
}

export default function AdvancedSignInOptions({
  method,
  onMethod,
  guidedDisabled,
  blockState,
  deviceCodeBlocked,
  controls,
}: AdvancedSignInOptionsProps) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const labelId = useId();
  const idBase = useId();
  const names = policyNames(blockState);
  const certain = blockState.kind === 'policies' && blockState.covers.length > 0;

  const options: { value: SignInMethod; label: string; description: string; badge?: ReactNode; disabled?: boolean }[] = [
    {
      value: 'browser',
      label: 'Microsoft sign-in (recommended)',
      description: 'Use the AirBridge helper to sign in from your browser.',
    },
    {
      value: 'device-code',
      label: 'Device code',
      description: 'Enter a code at microsoft.com/devicelogin.',
      disabled: guidedDisabled,
      badge: guidedDisabled ? (
        <Badge appearance="tint" color="subtle" size="small">
          Off for this deployment
        </Badge>
      ) : deviceCodeBlocked ? (
        <span className="inline-flex items-center gap-1">
          <Badge appearance="tint" color="warning" size="small" data-testid="device-code-restricted">
            Restricted by Conditional Access
          </Badge>
          <Tooltip
            relationship="description"
            content={`${certain ? 'Blocked' : 'May be blocked'} for the minting account by Conditional Access: ${names}. This affects device code only.`}
          >
            <Button
              appearance="transparent"
              size="small"
              aria-label={`Why device code is restricted: ${names}`}
              icon={<Info size={14} strokeWidth={1.5} />}
              style={{ minWidth: 0, padding: 0, height: 20 }}
            />
          </Tooltip>
        </span>
      ) : undefined,
    },
    {
      value: 'paste',
      label: 'Paste DRS/BPRT token',
      description: 'Use an existing token you obtained previously.',
    },
  ];

  return (
    <section
      style={{ border: `1px solid ${STROKE.base}`, borderRadius: RADIUS.lg, backgroundColor: SURFACE.base }}
      data-testid="advanced-sign-in-options"
    >
      <button
        type="button"
        className={`${PLAIN_BUTTON} flex w-full items-center gap-3 px-3 py-2.5 text-left focus-visible:outline focus-visible:outline-2`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        style={{ cursor: 'pointer' }}
      >
        <SlidersHorizontal size={18} strokeWidth={1.5} color={TEXT.secondary} aria-hidden="true" />
        <span className="flex min-w-0 flex-1 flex-col">
          <span id={labelId} className="text-[13.5px] font-semibold" style={{ color: TEXT.primary }}>
            Advanced sign-in options
          </span>
          <span className="text-[12px]" style={{ color: TEXT.secondary }}>
            {/* Collapsed with a non-default path chosen: say which, so the primary
                button's label is never a surprise. */}
            {!open && method !== 'browser'
              ? `Using: ${METHOD_NAMES[method]}`
              : 'Use device code or paste an existing DRS/BPRT token'}
          </span>
        </span>
        {open ? (
          <ChevronUp size={16} strokeWidth={1.5} color={TEXT.secondary} aria-hidden="true" />
        ) : (
          <ChevronDown size={16} strokeWidth={1.5} color={TEXT.secondary} aria-hidden="true" />
        )}
      </button>

      {open && (
        <div id={panelId} className="px-3 pb-3" style={{ borderTop: `1px solid ${STROKE.subtle}` }}>
          <RadioGroup
            aria-labelledby={labelId}
            value={method}
            onChange={(_, d) => onMethod(d.value as SignInMethod)}
            data-testid="sign-in-methods"
          >
            {options.map((opt) => {
              const selected = method === opt.value;
              const descId = `${idBase}-${opt.value}-desc`;
              return (
                <div key={opt.value} className="flex flex-col gap-1 pt-2" data-testid={`sign-in-option-${opt.value}`}>
                  <Radio
                    value={opt.value}
                    label={opt.label}
                    disabled={opt.disabled}
                    aria-describedby={descId}
                  />
                  <div className="flex flex-col gap-1 pl-8">
                    <span id={descId} className="-mt-1.5 text-[12px] leading-snug" style={{ color: TEXT.secondary }}>
                      {opt.description}
                    </span>
                    {opt.badge && <div>{opt.badge}</div>}
                    {selected && controls[opt.value] && (
                      <div className="pt-1" data-testid={`sign-in-controls-${opt.value}`}>
                        {controls[opt.value]}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </RadioGroup>
        </div>
      )}
    </section>
  );
}
